import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Search } from "lucide-react";

export type Column<T> = {
  /** Display label for the column header. */
  header: string;
  /** Optional css width (e.g. "12%"). */
  width?: string;
  /** Renders a single cell from the row. */
  cell: (row: T) => React.ReactNode;
  /** When true, the cell wraps and grows; otherwise it stays truncated. */
  wrap?: boolean;
  /** Optional className applied to the <td> element. */
  className?: string;
  /**
   * Hide this column on viewports narrower than the given Tailwind
   * breakpoint. Use to suppress non-essential signal (kyc level,
   * stripe badge, etc.) on phones without losing the row.
   *   * "sm" hides on <640px
   *   * "md" hides on <768px
   *   * "lg" hides on <1024px
   *   * "xl" hides on <1280px
   * The first column (usually name/email) should always stay visible
   * — omit the prop there.
   */
  hideBelow?: "sm" | "md" | "lg" | "xl";
};

function hideClass(bp: Column<unknown>["hideBelow"]): string {
  switch (bp) {
    case "sm":
      return "hidden sm:table-cell";
    case "md":
      return "hidden md:table-cell";
    case "lg":
      return "hidden lg:table-cell";
    case "xl":
      return "hidden xl:table-cell";
    default:
      return "";
  }
}

interface AdminTableProps<T> {
  /** API endpoint to fetch from; should accept `?q=` and `?limit=`/`?offset=`
   *  and return `{ items, total }` (where `items` is named via itemsKey). */
  endpoint: string;
  /** Key in the JSON response that holds the array of rows. */
  itemsKey: string;
  /** Column definitions, in render order. */
  columns: Column<T>[];
  /** Stable row key extractor. */
  rowKey: (row: T) => string;
  /** Placeholder for the search input. */
  searchPlaceholder?: string;
  /** Empty-state title/desc when there are no rows at all. */
  emptyTitle?: string;
  emptyDescription?: string;
  /** Optional right-side filters above the table. */
  filters?: React.ReactNode;
  /** Page size sent as `?limit=`. Defaults to 20 to match the backend default. */
  pageSize?: number;
}

/**
 * Generic admin list view. Owns search debounce + fetch lifecycle so each
 * admin page is just a column config + endpoint.
 */
export function AdminTable<T>({
  endpoint,
  itemsKey,
  columns,
  rowKey,
  searchPlaceholder = "Buscar",
  emptyTitle = "Nada por aqui ainda",
  emptyDescription,
  filters,
  pageSize = 20,
}: AdminTableProps<T>) {
  const [items, setItems] = useState<T[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Reset to page 1 whenever the active query changes — otherwise filtered
  // results may collapse below the current offset.
  useEffect(() => {
    setPage(1);
  }, [debouncedQuery]);

  useEffect(() => {
    let alive = true;
    const params = new URLSearchParams();
    if (debouncedQuery) params.set("q", debouncedQuery);
    params.set("limit", String(pageSize));
    params.set("offset", String((page - 1) * pageSize));
    const sep = endpoint.includes("?") ? "&" : "?";
    const url = `${endpoint}${sep}${params.toString()}`;
    fetch(url, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then((raw) => {
        if (!alive) return;
        const data = raw as Record<string, unknown>;
        const rows = (data[itemsKey] as T[]) ?? [];
        const t = typeof data.total === "number" ? data.total : rows.length;
        setItems(rows);
        setTotal(t);
      })
      .catch((err) => {
        if (!alive) return;
        setError(
          err === 403
            ? "Acesso negado. Esta página é exclusiva pra @zarv.com."
            : err === 401
              ? "Faça login pra continuar."
              : "Não foi possível carregar."
        );
      });
    return () => {
      alive = false;
    };
  }, [endpoint, debouncedQuery, itemsKey, page, pageSize]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (error) {
    return (
      <div className="rounded-3xl border border-danger/30 bg-danger/10 p-5 text-sm text-text">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search
            size={14}
            strokeWidth={2}
            className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-text-tertiary"
            aria-hidden
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full rounded-full border border-border bg-surface py-2.5 pl-9 pr-4 text-sm text-text outline-none placeholder:text-text-tertiary focus:border-accent focus:ring-2 focus:ring-accent/20"
          />
        </div>
        {filters}
        <p className="ml-auto hidden text-xs text-text-tertiary sm:block">
          {items === null
            ? "carregando…"
            : `${total} ${total === 1 ? "registro" : "registros"}`}
        </p>
      </div>

      {items === null ? (
        <div className="grid place-items-center rounded-3xl border border-border bg-surface py-16">
          <Loader2 size={20} className="animate-spin text-text-tertiary" aria-hidden />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-border bg-surface p-12 text-center">
          <h2 className="text-base font-semibold tracking-tight text-text">
            {debouncedQuery ? `Nenhum resultado para "${debouncedQuery}".` : emptyTitle}
          </h2>
          {emptyDescription && !debouncedQuery && (
            <p className="mx-auto mt-2 max-w-md text-sm text-text-secondary">
              {emptyDescription}
            </p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-border bg-surface">
          {/* `table-auto` lets the remaining visible columns share width
              when others collapse via `display:none`. Widths live on the
              <th> so hidden cells don't leave gaps. */}
          <table className="w-full table-auto">
            <thead>
              <tr className="border-b border-border bg-surface-alt text-[10px] font-medium uppercase tracking-[0.18em] text-text-tertiary">
                {columns.map((col, i) => (
                  <th
                    key={i}
                    scope="col"
                    style={col.width ? { width: col.width } : undefined}
                    className={`px-3 py-3 text-left sm:px-5 ${hideClass(col.hideBelow)}`}
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border text-sm">
              {items.map((row) => (
                <tr key={rowKey(row)} className="align-middle hover:bg-surface-alt">
                  {columns.map((col, i) => (
                    <td
                      key={i}
                      className={`px-3 py-3 sm:px-5 ${col.wrap ? "" : "truncate"} ${hideClass(col.hideBelow)} ${col.className ?? ""}`}
                    >
                      {col.cell(row)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <nav
          aria-label="Paginação"
          className="mt-5 flex items-center justify-between gap-4"
        >
          <p className="text-xs text-text-tertiary">
            Página {page} de {totalPages}
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft size={13} strokeWidth={2} aria-hidden />
              Anterior
            </button>
            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:bg-surface-alt disabled:cursor-not-allowed disabled:opacity-40"
            >
              Próxima
              <ChevronRight size={13} strokeWidth={2} aria-hidden />
            </button>
          </div>
        </nav>
      )}
    </div>
  );
}
